import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { ArrowLeft, Printer, AlertCircle } from 'lucide-react';

const AWBPreview = () => {
  const { docketId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const documentType = searchParams.get('document_type') || 'AIR_WAYBILL';

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      setError('Not authenticated. Please log in and try again.');
      return;
    }
    if (docketId) {
      // Pass token as query param — avoids blob: URL which is blocked in some environments
      setPreviewUrl(
        `${backendUrl}/api/dockets/${docketId}/awb/preview-pdf?token=${encodeURIComponent(token)}&document_type=${documentType}`
      );
    }
  }, [docketId, documentType]);

  const handlePrint = () => {
    const iframe = document.querySelector('[data-testid="awb-preview-iframe"]');
    try {
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.print();
      } else {
        window.print();
      }
    } catch (_) {
      window.print();
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#f3f4f6' }}>
      {/* Toolbar */}
      <div
        className="no-print"
        style={{
          background: '#1f2937',
          color: 'white',
          padding: '8px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Button
            data-testid="awb-preview-back-btn"
            variant="ghost"
            size="sm"
            style={{ color: 'white' }}
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <span style={{ fontSize: '14px', fontWeight: 600, letterSpacing: '0.02em' }}>
            Air Waybill Preview
          </span>
        </div>
        <Button
          data-testid="awb-preview-print-btn"
          size="sm"
          style={{ background: '#2563eb', color: 'white' }}
          onClick={handlePrint}
          disabled={!previewUrl}
        >
          <Printer className="w-4 h-4 mr-2" />
          Print / Save as PDF
        </Button>
      </div>

      {/* Content */}
      {error ? (
        <div
          data-testid="awb-preview-error"
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            color: '#dc2626',
          }}
        >
          <AlertCircle style={{ width: 40, height: 40 }} />
          <p style={{ fontWeight: 600 }}>Failed to load AWB PDF</p>
          <p style={{ fontSize: 14, color: '#6b7280', maxWidth: 400, textAlign: 'center' }}>{error}</p>
          <Button variant="outline" onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      ) : (
        <iframe
          data-testid="awb-preview-iframe"
          src={previewUrl || 'about:blank'}
          title="Air Waybill PDF"
          style={{ flex: 1, border: 'none', width: '100%', display: 'block' }}
        />
      )}

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { margin: 0; padding: 0; }
          iframe { position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; border: none; }
        }
      `}</style>
    </div>
  );
};

export default AWBPreview;
